import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import CommonPagination from 'components/commonPagination/CommonPagination';
import CommonTable from 'components/commonTable/CommonTable';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ListUserData } from 'reactRedux/thunks/userThunk/userThunk';
import PropTypes from 'prop-types';
import CommonSearch from 'components/commonSearchBar/CommonSearch';
import { useDebounce } from 'hooks/useDebounce';

const UserListModal = ({
  isOpen,
  onClose,
  setSelectedUsers,
  setSelectedUserName,
  setValue,
  currentSelectedUsers,
  currentSelectedUserNames,
}) => {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedUserNames, setSelectedUserNames] = useState([]);
  const dispatch = useDispatch();
  const { isLoading, userListData } = useSelector((state) => state.UserSlice);
  const debouncedSearch = useDebounce(search, 500);

  useEffect(() => {
    dispatch(
      ListUserData({
        page: page,
        limit: 10,
        search: debouncedSearch,
      }),
    );
  }, [page, dispatch, debouncedSearch]);

  const handleCheckboxChange = (id, name) => {
    setSelectedUserIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      } else {
        return [...prev, id];
      }
    });

    setSelectedUserNames((prev) => {
      if (prev.includes(name)) {
        return prev.filter((item) => item !== name);
      } else {
        return [...prev, name];
      }
    });
  };

  const handleSelectUser = () => {
    setSearch('');
    setSelectedUsers(selectedUserIds);
    setSelectedUserName(selectedUserNames.join('||'));
    setValue && setValue('userIds', selectedUserIds);

    setPage(1);
    onClose();
  };

  const onModalCloseIcon = () => {
    setSelectedUserIds([]);
    setSelectedUserNames([]);
    onClose();
    setPage(1);
    setSearch('');
  };

  useEffect(() => {
    if (currentSelectedUsers && currentSelectedUserNames) {
      setSelectedUserIds(currentSelectedUsers);
      setSelectedUserNames(
        typeof currentSelectedUserNames === 'string'
          ? currentSelectedUserNames.split('||')
          : currentSelectedUserNames,
      );
    } else {
      setSelectedUserIds([]);
      setSelectedUserNames([]);
    }
  }, [isOpen, currentSelectedUsers, currentSelectedUserNames]);

  const columnHelper = createColumnHelper();
  const columns = [
    columnHelper.accessor((row) => row?.userId, {
      id: 'selectUser',
      header: () => (
        <Text
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        ></Text>
      ),
      cell: (info) => {
        const userId = info.getValue();
        const userName =
          `${info.row.original.firstName || ''} ${info.row.original.lastName || ''}`.trim() ||
          'N/A';
        const isSelected = selectedUserIds.includes(userId);

        return (
          <Checkbox
            isChecked={isSelected}
            onChange={() => handleCheckboxChange(userId, userName)}
          />
        );
      },
    }),
    columnHelper.accessor((row) => row?.firstName, {
      id: 'firstName',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          whiteSpace="nowrap"
        >
          First Name
        </Text>
      ),
      cell: (info) => (
        <Flex align="center">
          <Text color={textColor} fontSize="sm" fontWeight="600" ml={2}>
            {info.getValue() || 'N/A'}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor((row) => row?.lastName, {
      id: 'lastName',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          whiteSpace="nowrap"
        >
          Last Name
        </Text>
      ),
      cell: (info) => (
        <Flex align="center">
          <Text color={textColor} fontSize="sm" fontWeight="600">
            {info.getValue() || 'N/A'}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor((row) => row?.email, {
      id: 'email',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          whiteSpace="nowrap"
        >
          Email
        </Text>
      ),
      cell: (info) => (
        <Flex align="center">
          <Text color={textColor} fontSize="sm" fontWeight="600">
            {info.getValue() || 'N/A'}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor((row) => row?.phone, {
      id: 'phoneNumber',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          whiteSpace="nowrap"
        >
          Phone Number
        </Text>
      ),
      cell: (info) => (
        <Flex align="center">
          <Text color={textColor} fontSize="sm" fontWeight="600">
            {info.getValue() ? `+${info.getValue()}` : 'N/A'}
          </Text>
        </Flex>
      ),
    }),
  ];

  return (
    <Box>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="5xl"
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader position={'relative'}>
            <Flex
              justify="space-between"
              align="center"
              width="100%"
              paddingRight="48px"
              flexWrap={'wrap'}
              gap={4}
            >
              <Text fontSize="xl" fontWeight="bold" whiteSpace="nowrap">
                User List
              </Text>

              <Box width="250px">
                <CommonSearch
                  search={search}
                  setSearch={setSearch}
                  setPage={setPage}
                />
              </Box>

              <ModalCloseButton
                position={'absolute'}
                right={{ base: '16px', md: '32px' }}
                top={{ base: '16px', md: '21px' }}
                onClick={() => onModalCloseIcon()}
              />
            </Flex>
          </ModalHeader>
          <ModalBody>
            <CommonTable
              columns={columns}
              data={userListData?.list}
              isLoading={isLoading}
              keyValue="userId"
              rowclick={['actions', 'status']}
              excludes={['actions', 'status']}
              dynamicWidth={{
                selectUser: '20px',
                firstName: '50px',
                lastName: '50px',
                email: '190px',
                phoneNumber: '190px',
              }}
            />
            <CommonPagination
              totalCount={userListData?.total || 0}
              activePage={page}
              pageCount={Math.ceil(
                (userListData?.total || 0) / (userListData?.limit || 10),
              )}
              onPageChange={(e) => setPage(e)}
              itemsPerPage={userListData?.limit || 10}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              onClick={handleSelectUser}
              isDisabled={selectedUserIds.length === 0}
            >
              Submit
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

UserListModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  setSelectedUsers: PropTypes.func.isRequired,
  setSelectedUserName: PropTypes.func.isRequired,
  setValue: PropTypes.func,
  currentSelectedUsers: PropTypes.array,
  currentSelectedUserNames: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.string,
  ]),
};

export default UserListModal;
